import React, { useState } from 'react';

interface DataPoint {
  date: string;
  value: number;
}

interface ValueChartProps {
  data: DataPoint[];
  loading?: boolean;
  height?: number;
}

type TimeRange = '7d' | '30d' | '90d' | 'all';

export default function ValueChart({
  data,
  loading = false,
  height = 200
}: ValueChartProps) {
  const [timeRange, setTimeRange] = useState<TimeRange>('30d');

  // Filter data based on time range
  const filteredData = React.useMemo(() => {
    if (data.length === 0) return [];

    const now = new Date();
    let cutoff: Date;

    switch (timeRange) {
      case '7d':
        cutoff = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case '30d':
        cutoff = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case '90d':
        cutoff = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        break;
      default:
        return data;
    }

    return data.filter(d => new Date(d.date) >= cutoff);
  }, [data, timeRange]);

  // Calculate chart dimensions
  const minValue = Math.min(...filteredData.map(d => d.value), 0);
  const maxValue = Math.max(...filteredData.map(d => d.value), 1);
  const range = maxValue - minValue || 1;

  // Generate SVG path
  const generatePath = () => {
    if (filteredData.length === 0) return '';

    const width = 100;
    const points = filteredData.map((d, i) => {
      const x = (i / (filteredData.length - 1 || 1)) * width;
      const y = 100 - ((d.value - minValue) / range) * 100;
      return `${x},${y}`;
    });

    return `M${points.join(' L')}`;
  };

  // Generate gradient fill path
  const generateFillPath = () => {
    if (filteredData.length === 0) return '';

    const path = generatePath();
    const width = 100;
    return `${path} L${width},100 L0,100 Z`;
  };

  const timeRanges: { key: TimeRange; label: string }[] = [
    { key: '7d', label: '7D' },
    { key: '30d', label: '30D' },
    { key: '90d', label: '90D' },
    { key: 'all', label: 'All' },
  ];

  const formatValue = (val: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
      notation: 'compact',
    }).format(val);
  };

  if (loading) {
    return (
      <div className="bg-gray-900/50 rounded-2xl p-6 border border-gray-800">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-700 rounded w-32 mb-4" />
          <div className="h-48 bg-gray-800 rounded" />
        </div>
      </div>
    );
  }

  const startValue = filteredData[0]?.value || 0;
  const endValue = filteredData[filteredData.length - 1]?.value || 0;
  const isPositive = endValue >= startValue;

  return (
    <div className="bg-gray-900/50 rounded-2xl p-6 border border-gray-800">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">Value History</h3>
        <div className="flex gap-1 bg-gray-800 rounded-lg p-1">
          {timeRanges.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setTimeRange(key)}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                timeRange === key
                  ? 'bg-gray-700 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {filteredData.length === 0 ? (
        <div
          className="flex flex-col items-center justify-center text-center"
          style={{ height }}
        >
          <p className="text-gray-500">Price history coming soon</p>
          <p className="text-sm text-gray-600 mt-1">
            Historical value tracking will appear here
          </p>
        </div>
      ) : (
        <div className="relative" style={{ height }}>
          {/* Y-axis labels */}
          <div className="absolute left-0 top-0 bottom-0 w-12 flex flex-col justify-between text-right text-xs text-gray-500 pr-2">
            <span>{formatValue(maxValue)}</span>
            <span>{formatValue((maxValue + minValue) / 2)}</span>
            <span>{formatValue(minValue)}</span>
          </div>

          {/* Chart */}
          <div className="absolute left-14 right-0 top-0 bottom-0">
            <svg
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              className="w-full h-full"
            >
              {/* Gradient definition */}
              <defs>
                <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="0%"
                    stopColor={isPositive ? '#22C55E' : '#EF4444'}
                    stopOpacity="0.3"
                  />
                  <stop
                    offset="100%"
                    stopColor={isPositive ? '#22C55E' : '#EF4444'}
                    stopOpacity="0"
                  />
                </linearGradient>
              </defs>

              {/* Grid lines */}
              <line x1="0" y1="25" x2="100" y2="25" stroke="#374151" strokeWidth="0.5" />
              <line x1="0" y1="50" x2="100" y2="50" stroke="#374151" strokeWidth="0.5" />
              <line x1="0" y1="75" x2="100" y2="75" stroke="#374151" strokeWidth="0.5" />

              {/* Fill */}
              <path
                d={generateFillPath()}
                fill="url(#chartGradient)"
              />

              {/* Line */}
              <path
                d={generatePath()}
                fill="none"
                stroke={isPositive ? '#22C55E' : '#EF4444'}
                strokeWidth="2"
                vectorEffect="non-scaling-stroke"
              />
            </svg>
          </div>

          {/* X-axis labels */}
          <div className="absolute left-14 right-0 bottom-0 translate-y-full pt-2 flex justify-between text-xs text-gray-500">
            <span>{filteredData[0]?.date || ''}</span>
            <span>{filteredData[filteredData.length - 1]?.date || ''}</span>
          </div>
        </div>
      )}
    </div>
  );
}
